/**
 * **Randpfeile** — in welchen Richtungen außerhalb des sichtbaren
 * **Ausschnitts** noch **Canvas-Elemente** liegen. Siehe „Randpfeil" in
 * CONTEXT.md.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`: die Rechnung soll ohne
 * gerenderten Canvas prüfbar sein.
 */

import type { Bounds } from './canvasAlignment'

/**
 * Die acht Richtungen: vier **Kantenpfeile** und vier **Eckpfeile**.
 *
 * Acht flache Booleans und kein `Set`: `sameDirections` entscheidet, ob
 * überhaupt neu gerendert wird, und läuft bei jeder Store-Änderung — beim
 * Schieben also in jedem Frame. Ein Feldvergleich ist dort das Billigste, und
 * `NO_OFFSCREEN` behält seine stabile Identität.
 */
export interface OffscreenDirections {
  left: boolean
  right: boolean
  top: boolean
  bottom: boolean
  topLeft: boolean
  topRight: boolean
  bottomLeft: boolean
  bottomRight: boolean
}

/**
 * Keine Richtung. Als Konstante, damit „nichts draußen" nicht bei jedem
 * Aufruf eine neue Identität bekommt — dieselbe Vorsicht wie bei `NO_GUIDES`
 * in `CanvasFlow`.
 */
export const NO_OFFSCREEN: OffscreenDirections = {
  left: false,
  right: false,
  top: false,
  bottom: false,
  topLeft: false,
  topRight: false,
  bottomLeft: false,
  bottomRight: false,
}

/**
 * In welchen Richtungen außerhalb des Ausschnitts noch Elemente liegen.
 *
 * **Gezählt wird, was nicht GANZ zu sehen ist** — nicht erst, was gar nicht zu
 * sehen ist. Eine Notiz, von dem ein Zipfel am Rand hereinragt, ist
 * angekommen und trotzdem nicht angeschaut; der Pfeil bleibt stehen, bis er
 * vollständig im Bild liegt.
 *
 * Die Grenze zieht `isFullyVisible`, dieselbe Funktion, an der auch die
 * **Tab-Reihenfolge** entscheidet, ob der Ausschnitt nachrücken muss
 * (`CanvasFlow`). Beide Stellen beantworten dieselbe Frage — „ist das Ziel
 * erreicht?" — und sollen sie nicht verschieden beantworten.
 *
 * Nicht `isVisible`: Das genügt beim **Ausrichten**, wo schon die Berührung
 * zählt (man richtet sich an dem aus, was man sieht). Hier geht es um etwas
 * anderes.
 *
 * **Klassifiziert wird je ELEMENT, nicht je Kante.** Jedes unsichtbare
 * Element bekommt für sich genau EINE der acht Richtungen; gezeichnet wird die
 * Vereinigung. Der Unterschied ist der ganze Zweck dieser Funktion:
 *
 * | Elemente | je Kante (früher) | je Element |
 * |---|---|---|
 * | eines unten rechts | rechts UND unten | die Ecke unten rechts |
 * | eines rechts, eines unten | rechts UND unten | rechts UND unten |
 *
 * Vier unabhängige Kanten-Flags konnten nicht auseinanderhalten, ob zwei
 * Aussagen von einem Element stammen oder von zweien. Beim einzelnen diagonal
 * liegenden Element führte das in die Irre: Wer dem „rechts" folgt, findet
 * dort nichts, weil es weiter unten liegt.
 *
 * Die Einteilung ist BINÄR („ganz draußen auf dieser Achse"), nicht nach
 * Winkel. Bei schiefen Lagen sieht das falsch aus und ist trotzdem richtig:
 * Ein Element 2000px rechts und 1px unter der Kante bekommt die Diagonale,
 * denn nur sie führt hin — wer nur nach rechts schöbe, bekäme es nie zu sehen.
 * Der Pfeil verspricht „folge mir, dann findest du es", nicht „dort drüben,
 * ungefähr in diesem Winkel".
 *
 * @param rects    die Elemente, in Canvas-Koordinaten
 * @param viewport der sichtbare Ausschnitt, ebenfalls in Canvas-Koordinaten
 */
export function getOffscreenDirections(
  rects: Bounds[],
  viewport: Bounds,
): OffscreenDirections {
  const found = { ...NO_OFFSCREEN }
  for (const rect of rects) markOffscreen(found, rect.left, rect.top, rect.right, rect.bottom, viewport)
  return found
}

/**
 * EIN Element in ein Ergebnis eintragen — die vier Kanten als Zahlen, kein
 * `Bounds`-Objekt.
 *
 * **Der Grund für diese Form ist der Aufrufer:** Der Selektor in
 * `CanvasOffscreenHints` läuft bei JEDER Store-Änderung über alle Nodes, beim
 * Pannen also je Frame. Ein Array aus `Bounds` je Durchgang hieße je Node und
 * Frame ein neues Objekt — reine Müllproduktion, weil die Zahlen schon im
 * Store liegen. `getOffscreenDirections` ist die Hülle darum für alle, die
 * bereits `Bounds` in der Hand haben; die Regeln stehen nur hier.
 */
export function markOffscreen(
  found: OffscreenDirections,
  left: number,
  top: number,
  right: number,
  bottom: number,
  viewport: Bounds,
): void {
  // Ganz im Bild — nichts zu melden.
  if (
    left >= viewport.left && right <= viewport.right &&
    top >= viewport.top && bottom <= viewport.bottom
  ) return

  /*
    Je Achse: ragt es über die eine Kante hinaus, über die andere — oder
    liegt es dazwischen (`null`)?

    **Es genügt, dass es übersteht.** Ganz jenseits der Kante zu liegen ist
    nicht mehr verlangt: Sonst bekäme ein Element, das halb hereinragt, gar
    keine Richtung und der Pfeil verschwände, obwohl man noch nachfahren muss.

    Ein Element, das den Ausschnitt auf einer Achse ÜBERSPANNT — links wie
    rechts hinaus —, zählt dort als erledigt. Es ganz zu sehen ist auf dieser
    Achse unmöglich, und ein Pfeil, den man durch Fahren nicht loswird, wäre
    keine Auskunft, sondern ein Dauerzustand.
  */
  const overLeft = left < viewport.left
  const overRight = right > viewport.right
  const overTop = top < viewport.top
  const overBottom = bottom > viewport.bottom

  const horizontal = overLeft && overRight ? null
    : overLeft ? 'left'
      : overRight ? 'right'
        : null
  const vertical = overTop && overBottom ? null
    : overTop ? 'top'
      : overBottom ? 'bottom'
        : null

  if (vertical === 'top' && horizontal === 'left') found.topLeft = true
  else if (vertical === 'top' && horizontal === 'right') found.topRight = true
  else if (vertical === 'bottom' && horizontal === 'left') found.bottomLeft = true
  else if (vertical === 'bottom' && horizontal === 'right') found.bottomRight = true
  else if (vertical) found[vertical] = true
  else if (horizontal) found[horizontal] = true
  // Kein `else`: Ein Element, das den Ausschnitt in BEIDEN Achsen überspannt,
  // bekommt keine Richtung. Man sieht seine Mitte, mehr geht nicht.
}

/**
 * Ob von diesem Element ÜBERHAUPT etwas zu sehen ist — ein Zipfel genügt.
 *
 * **Die andere Frage als die des Randpfeils.** Der fragt „kann ich es lesen?"
 * und bleibt deshalb stehen, bis das Element GANZ im Bild ist. Die **Karte**
 * fragt „weiß ich, wo ich bin?", und dafür genügt ein Anhaltspunkt: Wer eine
 * Zettelecke oder die Fläche einer **Mappe** sieht, ist nicht verloren.
 *
 * Die strenge Schwelle stand zuerst hier und war im Gebrauch zu eifrig: Auf
 * einem Canvas, auf dem mehrere Zettel und ein großer Bereich zu sehen waren,
 * aber keiner davon ganz, blitzte die Karte auf (Sven, 2026-09-08, mit Bild).
 * Ein überspannendes Element braucht damit auch keine Ausnahme mehr — es
 * überlappt ja.
 *
 * Als vier Zahlen und nicht als `Bounds`, aus demselben Grund wie bei
 * `markOffscreen`: Der Aufrufer läuft je Bild über alle Elemente.
 */
export function overlapsViewport(
  left: number,
  top: number,
  right: number,
  bottom: number,
  viewport: Bounds,
): boolean {
  return right > viewport.left && left < viewport.right &&
    bottom > viewport.top && top < viewport.bottom
}

/**
 * Ob zwei Ergebnisse dasselbe sagen.
 *
 * Der Vergleich entscheidet, ob überhaupt neu gerendert wird: Die Auswertung
 * läuft bei JEDER Store-Änderung, also während des Schiebens jeden Frame — die
 * acht Werte wechseln aber nur in dem Moment, in dem ein Element eine Kante
 * überquert.
 */
export function sameDirections(a: OffscreenDirections, b: OffscreenDirections): boolean {
  return a.left === b.left && a.right === b.right &&
    a.top === b.top && a.bottom === b.bottom &&
    a.topLeft === b.topLeft && a.topRight === b.topRight &&
    a.bottomLeft === b.bottomLeft && a.bottomRight === b.bottomRight
}
